import { FC, ReactElement, memo } from 'react';
import ReportTableColumnTitle from '../ReportTableColumnTitle/ReportTableColumnTitle';
import { ICustomTooltipProps } from '~/view/components/Controls/CustomTooltip/CustomTooltip';
import { defaultReportsTableTitles } from '~/view/screens/ReportsScreen/constants/reprots';
import { REPORTS_BODY_KEY, REPORT_TYPE } from '~/view/screens/ReportsScreen/types/reports';

interface IProps {
  reportColumnName: string; // e.g. item, agent, month
  columnTooltips?: { [key in REPORTS_BODY_KEY]?: Partial<ICustomTooltipProps> };
  disabledRevenue?: boolean;
  reportType?: REPORT_TYPE;
}

const orderCountKeys = {
  [REPORT_TYPE.ITEMS]: REPORTS_BODY_KEY.ITEMS_COUNT,
  [REPORT_TYPE.COMBO]: REPORTS_BODY_KEY.TOTAL_NUMBER_OF_COMBO,
  default: REPORTS_BODY_KEY.ORDERS_COUNT,
};

const pickupCountKeys = {
  [REPORT_TYPE.ITEMS]: REPORTS_BODY_KEY.PICKUP_ITEMS_COUNT,
  [REPORT_TYPE.COMBO]: REPORTS_BODY_KEY.PICKUP_NUMBER_OF_COMBO,
  default: REPORTS_BODY_KEY.PICKUP_COUNT,
};

const deliveryCountKeys = {
  [REPORT_TYPE.ITEMS]: REPORTS_BODY_KEY.DELIVERY_ITEMS_COUNT,
  [REPORT_TYPE.COMBO]: REPORTS_BODY_KEY.DELIVERY_NUMBER_OF_COMBO,
  default: REPORTS_BODY_KEY.DELIVERY_COUNT,
};

const dineInCountKeys = {
  [REPORT_TYPE.ITEMS]: REPORTS_BODY_KEY.DINEIN_ITEMS_COUNT,
  [REPORT_TYPE.COMBO]: REPORTS_BODY_KEY.DINEIN_NUMBER_OF_COMBO,
  default: REPORTS_BODY_KEY.DINEIN_COUNT,
};

const ReportTableColumnTitles: FC<IProps> = ({ reportColumnName, columnTooltips, disabledRevenue, reportType }): ReactElement => {
  const COLUMN_TITLES = {
    ORDERS_COUNT: orderCountKeys[reportType as keyof typeof orderCountKeys] || orderCountKeys.default,
    REVENUE: REPORTS_BODY_KEY.REVENUE,
    AVERAGE_CHECK: REPORTS_BODY_KEY.AVERAGE_CHECK,
    PICKUP_COUNT: pickupCountKeys[reportType as keyof typeof pickupCountKeys] || pickupCountKeys.default,
    PICKUP_REVENUE: REPORTS_BODY_KEY.PICKUP_REVENUE,
    DELIVERY_COUNT: deliveryCountKeys[reportType as keyof typeof deliveryCountKeys] || deliveryCountKeys.default,
    DELIVERY_REVENUE: REPORTS_BODY_KEY.DELIVERY_REVENUE,
    DINEIN_COUNT: dineInCountKeys[reportType as keyof typeof dineInCountKeys] || dineInCountKeys.default,
    DINEIN_REVENUE: REPORTS_BODY_KEY.DINEIN_REVENUE,
    ...(reportType === REPORT_TYPE.COMBO ? { COMBO_PRICE: REPORTS_BODY_KEY.TOTAL_COMBO_PRICE } : {}),
  };

  return (
    <div className="d-flex flex-row mb-4">
      <ReportTableColumnTitle title={reportColumnName} />
      <ReportTableColumnTitle
        title={defaultReportsTableTitles[COLUMN_TITLES.ORDERS_COUNT]}
        columnName={REPORTS_BODY_KEY.ORDERS_COUNT}
        tooltipProps={columnTooltips?.[REPORTS_BODY_KEY.ORDERS_COUNT]}
      />
      {!disabledRevenue && (
        <>
          <ReportTableColumnTitle
            title={defaultReportsTableTitles[COLUMN_TITLES.REVENUE]}
            columnName={REPORTS_BODY_KEY.REVENUE}
            tooltipProps={columnTooltips?.[REPORTS_BODY_KEY.REVENUE]}
          />
          <ReportTableColumnTitle
            title={defaultReportsTableTitles[COLUMN_TITLES.AVERAGE_CHECK]}
            columnName={REPORTS_BODY_KEY.AVERAGE_CHECK}
            tooltipProps={columnTooltips?.[REPORTS_BODY_KEY.AVERAGE_CHECK]}
          />
        </>
      )}
      <ReportTableColumnTitle
        title={defaultReportsTableTitles[COLUMN_TITLES.PICKUP_COUNT]}
        columnName={REPORTS_BODY_KEY.PICKUP_COUNT}
        tooltipProps={columnTooltips?.[REPORTS_BODY_KEY.PICKUP_COUNT]}
      />
      {!disabledRevenue && (
        <ReportTableColumnTitle
          title={defaultReportsTableTitles[COLUMN_TITLES.PICKUP_REVENUE]}
          columnName={REPORTS_BODY_KEY.PICKUP_REVENUE}
          tooltipProps={columnTooltips?.[REPORTS_BODY_KEY.PICKUP_REVENUE]}
        />
      )}
      <ReportTableColumnTitle
        title={defaultReportsTableTitles[COLUMN_TITLES.DELIVERY_COUNT]}
        columnName={REPORTS_BODY_KEY.DELIVERY_COUNT}
        tooltipProps={columnTooltips?.[REPORTS_BODY_KEY.DELIVERY_COUNT]}
      />
      {!disabledRevenue && (
        <ReportTableColumnTitle
          title={defaultReportsTableTitles[COLUMN_TITLES.DELIVERY_REVENUE]}
          columnName={REPORTS_BODY_KEY.DELIVERY_REVENUE}
          tooltipProps={columnTooltips?.[REPORTS_BODY_KEY.DELIVERY_REVENUE]}
        />
      )}
      <ReportTableColumnTitle
        title={defaultReportsTableTitles[COLUMN_TITLES.DINEIN_COUNT]}
        columnName={REPORTS_BODY_KEY.DINEIN_COUNT}
        tooltipProps={columnTooltips?.[REPORTS_BODY_KEY.DINEIN_COUNT]}
      />
      {!disabledRevenue && (
        <ReportTableColumnTitle
          title={defaultReportsTableTitles[COLUMN_TITLES.DINEIN_REVENUE]}
          columnName={REPORTS_BODY_KEY.DELIVERY_REVENUE}
          tooltipProps={columnTooltips?.[REPORTS_BODY_KEY.DELIVERY_REVENUE]}
        />
      )}
      {reportType === REPORT_TYPE.COMBO && !!COLUMN_TITLES.COMBO_PRICE && (
        <ReportTableColumnTitle
          title={defaultReportsTableTitles[COLUMN_TITLES.COMBO_PRICE]}
          columnName={REPORTS_BODY_KEY.TOTAL_COMBO_PRICE}
          tooltipProps={columnTooltips?.[REPORTS_BODY_KEY.TOTAL_COMBO_PRICE]}
        />
      )}
    </div>
  );
};

export default memo(ReportTableColumnTitles);
